import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs,
  Button,
  Descriptions,
  Badge,
  Divider,
  Typography,
  Space,
  List,
  Avatar,
  Tag,
  Dropdown,
  Modal,
  message,
  Col,
  Row,
  Spin,
  Alert,
  Tooltip,
} from 'antd';
import {
  DownOutlined,
  EllipsisOutlined,
  UserDeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import Menu, { MenuProps } from 'antd/es/menu/menu';
import {
  Address,
  Decedent,
  Estate,
  ExecutorInviteResponse,
  Executor,
  ParticipantResponse,
  User,
} from '../../types';
import Cookies from 'js-cookie';
import moment from 'moment';
import AddEstateModal from './addestate';
import DecedentModal from './decedentform';
import EditOwnerModal from './editowner';
import AddExecutorModal from '../executor/addexecutor';
import AddParticipantModal from '../participant/addparticipant';
import ImportExportModal from '../../components/ImportExportModal';
import { deleteParticipant } from '../participant/participantService';
import { addExecutorToParticipants, deleteEstate, fetchEstateDetails } from './estateService';
import AuditTrail from '../audittrail/audittrail';
import { stateNameOf } from './usstates';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const getInitials = (firstName?: string | null, lastName?: string | null, email?: string | null) => {
  if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (lastName) return lastName.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'U';
};

const fullName = (person?: { prefix?: string | null; firstName?: string | null; middleName?: string | null; lastName?: string | null; suffix?: string | null } | null) =>
  [person?.prefix, person?.firstName, person?.middleName, person?.lastName, person?.suffix]
    .filter(Boolean)
    .join(' ');

/**
 * The decedent's own addresses, shown as theirs.
 *
 * They are not the estate's: the estate is a separate legal person with an
 * address of its own, recorded on the estate form. Mailing is stored under
 * "mailing" and physical under "domicile".
 */
const addressOfType = (decedent?: Decedent | null, addressType?: string) =>
  decedent?.addresses?.find((a) => a.addressType?.toLowerCase() === addressType);

const formatAddress = (address?: Partial<Address> | null) => {
  if (!address) return '';

  const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ');
  const region = [address.city, address.state, address.zipCode].filter(Boolean).join(' ');
  const county = address.county ? `${address.county} County` : '';

  return [street, region, county].filter(Boolean).join(', ');
};

/** The estate's own address, held on the estate rather than on any person. */
const estateAddress = (estate?: Estate | null): Partial<Address> => ({
  addressLine1: estate?.addressLine1 || '',
  addressLine2: estate?.addressLine2 || '',
  city: estate?.city || '',
  state: estate?.state || '',
  zipCode: estate?.zipCode || '',
  county: estate?.county || '',
});

// Styled Components
const EstateContainer = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  /* The actions outgrew one row once the estate, owner and decedent each had
     their own. Wrapping puts them on a second line instead of overflowing the
     card and clipping the last of them. */
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

/*
 * Flex items shrink below their content by default, which squeezed the estate
 * title into a column one character wide once the actions needed the room.
 */
const HeaderTitle = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  margin-bottom: 0.5rem;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  &:hover {
    border-color: #3b82f6;
  }
`;

const ParticipantDetails = styled.div`
  flex-grow: 1;
  margin-left: 1rem;
`;

const ParticipantActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EstateDetails: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const [role, setRole] = useState<string>('Admin');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDecedentModalVisible, setIsDecedentModalVisible] = useState(false);
  const [isEditOwnerModalVisible, setIsEditOwnerModalVisible] = useState(false);
  const [isAddParticipantModalVisible, setIsAddParticipantModalVisible] = useState(false);
  const [isAddExecutorModalVisible, setIsAddExecutorModalVisible] = useState(false);
  const [isImportExportModalVisible, setIsImportExportModalVisible] = useState(false);

  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [executors, setExecutors] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<User[]>([]);

  const [invitedGuests, setInvitedGuests] = useState<any[]>([]);

  const [estateDetails, setEstateDetails] = useState<Estate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // For editing a participant
  const [participantToEdit, setParticipantToEdit] = useState<ParticipantResponse | null>(null);

  // Role Menu items and handler
  const items: MenuProps['items'] = [
    { label: 'User', key: 'User' },
    { label: 'Guest', key: 'Guest' },
    { label: 'Admin', key: 'Admin', danger: true, disabled: true },
    { label: 'Executor', key: 'Executor', danger: true },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    message.info(`Role changed to ${e.key}`);
    handleRoleChange(e.key);
  };

  const handleRoleChange = (newRole: string) => {
    Modal.confirm({
      title: 'Confirm Role Change',
      content: `Are you sure you want to change the role to ${newRole}?`,
      onOk: () => setRole(newRole),
      okText: 'Yes',
      cancelText: 'No',
    });
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const RoleMenu = (
    <Dropdown menu={menuProps}>
      <Button>
        <Space>
          Change Role
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );

  /*
   * Estate-level actions. The three things an estate holds are edited apart:
   * the estate itself has its own button, and the two people, its owner and its
   * decedent, have an entry each here. They are separate records, and a
   * correction to one should never rewrite the other.
   */
  const estateActionsMenuItems: MenuProps['items'] = [
    {
      label: 'Edit Owner',
      key: 'editOwner',
    },
    {
      label: estateDetails?.decedent ? 'Edit Decedent' : 'Add Decedent',
      key: 'decedent',
    },
    { type: 'divider' },
    {
      label: 'Delete Estate',
      key: 'delete',
      danger: true,
    },
  ];

  // Handle click on the estate actions menu
  const handleEstateActionClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'editOwner') {
      setIsEditOwnerModalVisible(true);
    }
    if (e.key === 'decedent') {
      setIsDecedentModalVisible(true);
    }
    if (e.key === 'delete') {
      handleDeleteEstate();
    }
  };

  const estateActionsMenuProps = {
    items: estateActionsMenuItems,
    onClick: handleEstateActionClick,
  };

  // Function to delete the estate
  const handleDeleteEstate = () => {
    Modal.confirm({
      title: 'Confirm Estate Deletion',
      content: 'Are you sure you want to delete this estate?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteEstate(estateId!, token);
          message.success('Estate deleted successfully');
          navigate('/estates'); // Navigate back to the estates list page (adjust as needed)
        } catch (error: any) {
          console.error(error);
          message.error(error.message || 'Failed to delete estate');
        }
      },
    });
  };

  const handleAddExecutorToParticipant = async (executorUid: string) => {
    try {
      const token = Cookies.get('token') || Cookies.get('accessToken');
      if (!token || !estateId) {
        message.error('Unauthorized. Please log in.');
        return;
      }
  
      await addExecutorToParticipants(estateId, executorUid);
      
      // Refresh the estate details to show the new participant
      const updatedEstate = await fetchEstateDetails(estateId, token);
      setEstateDetails(updatedEstate);
      setParticipants(updatedEstate.participants as ParticipantResponse[]);
      
      message.success('Executor added to participants successfully');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to add executor to participants');
    }
  };

  // Fetch Estate Details on Mount (for viewing/editing existing estate)
  useEffect(() => {
    const loadEstateDetails = async () => {
      try {
        const token = Cookies.get('token') || Cookies.get('accessToken'); // Adjust the cookie name as needed
        if (!token) {
          message.error('Unauthorized. Please log in.');
          navigate('/login'); // Redirect to login or appropriate page
          return;
        }

        const estateData: Estate = await fetchEstateDetails(estateId!, token);

        setEstateDetails({
          ...estateData,
        });

        // Participants
        setParticipants(estateData.participants as ParticipantResponse[]);

        // Executors
        setExecutors(estateData.executor);

        // Guests
        setInvitedGuests(estateData.guests);

        // Invites
        setInvites(estateData.invites);

        // Users
        setUsers(estateData.users);

        setIsLoading(false);
      } catch (err: any) {
        console.error(err);
        if (err.message === 'Unauthorized') {
          message.error('Unauthorized. Please log in.');
          navigate('/login');
        } else {
          setError(err.message || 'Failed to load estate details.');
        }
        setIsLoading(false);
      }
    };

    // Only fetch if estateId is provided (i.e., viewing/editing an existing estate)
    if (estateId) {
      loadEstateDetails();
    } else {
      setIsLoading(false); // No estate to load
    }
  }, [estateId, navigate]);

  // Handle Success of Estate Edit or Create
  const handleEstateSubmit = (estate: Estate) => {
    if (estateId) {
      // Editing existing estate
      setEstateDetails(estate);
      message.success('Estate details updated successfully');
    } else {
      // Creating new estate
      setEstateDetails(estate);
      message.success('Estate created successfully');
      // Optionally, navigate to the new estate's details page
      navigate(`/estates/${estate.uuid}`);
    }
  };

  // The owner and decedent forms return the whole estate, and announce their
  // own success, so this only needs to take the fresh copy.
  const handleEstatePersonSaved = (estate: Estate) => {
    setEstateDetails(estate);
  };

  // Handle Success of Adding or Editing a Participant
  const handleParticipantSuccess = (participant: ParticipantResponse) => {
    if (participantToEdit) {
      // Editing existing participant
      setParticipants((prev) =>
        prev.map((p) => (p.id === participant.id ? participant : p))
      );
      setParticipantToEdit(null);
    } else {
      // Adding new participant

      setParticipants((prev) => [...prev, participant]);
    }
    message.success(`Participant ${participantToEdit ? 'updated' : 'added'} successfully`);
  };

  // Handle Success of Executor Invitation
  const handleExecutorInviteSuccess = (response: ExecutorInviteResponse) => {
    // Optionally, refresh the executors list or append the new executor if API returns executor data
    message.success(response.message);
    // If the executor appears in the list immediately, you can append it as follows:
    // setExecutors([...executors, newExecutor]);
  };

  // Handle Deleting a Participant
  const handleDeleteParticipant = (participantId: string) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this participant?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteParticipant(estateId!, participantId, token);
          setParticipants((prev) => prev.filter((p) => p.uid !== participantId));
          message.success('Participant deleted successfully');
        } catch (error: any) {
          console.error(error);
          message.error(error.message || 'Failed to delete participant');
        }
      },
    });
  };

  // Handle Editing a Participant
  const handleEditParticipant = (participant: ParticipantResponse) => {
    setParticipantToEdit(participant);
    setIsAddParticipantModalVisible(true);
  };

  // Two addresses, kept apart on purpose. The estate's is the estate's, and
  // the decedent's is shown under their name.
  const estateAddressText = formatAddress(estateAddress(estateDetails));
  const decedentAddressText = formatAddress(
    addressOfType(estateDetails?.decedent, 'domicile') ||
      addressOfType(estateDetails?.decedent, 'mailing'),
  );

  if (isLoading) {
    return (
      <EstateContainer>
        <Spin tip="Loading estate details..." />
      </EstateContainer>
    );
  }

  if (error) {
    return (
      <EstateContainer>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        />
      </EstateContainer>
    );
  }

  return (
    <Row gutter={16} style={{ marginTop: '24px' }}>
      {/* Estate Details and Actions */}
      <Col span={16}>
        <EstateContainer>
          {/* Custom Header */}
          <HeaderContainer>
            <HeaderTitle>
              <Space direction="vertical">
                <Title level={4}>Estate {estateId ? `#${estateId.slice(0, -12)}` : ''}</Title>
                <Space>
                  <Badge status="processing" text="In progress" />
                  <Text type="secondary">In progress</Text>
                </Space>
              </Space>
            </HeaderTitle>
            <Space wrap>
              {estateId && (
                <>
                  <Tooltip title="Bulk Import/Export Assets, Liabilities, Participants, and Cashflows">
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={() => setIsImportExportModalVisible(true)}
                    >
                      Import/Export
                    </Button>
                  </Tooltip>
                  <Button
                    type="primary"
                    onClick={() => setIsEditModalVisible(true)}
                  >
                    Edit Estate
                  </Button>
                </>
              )}
              {estateId ? (
                <>
                  <Button onClick={() => setIsAddParticipantModalVisible(true)}>
                    Add Participant
                  </Button>
                  <Button onClick={() => setIsAddExecutorModalVisible(true)}>
                    Add Executor
                  </Button>
                </>
              ) : (
                <Button onClick={() => navigate('/create-estate')}>
                  Create Estate
                </Button>
              )}
              <Dropdown menu={estateActionsMenuProps}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </Space>
          </HeaderContainer>

          {estateId && (
            <>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Estate Name">
                  {estateDetails?.estateName}
                </Descriptions.Item>
                <Descriptions.Item label="Owner">
                  {fullName(estateDetails?.estateOwner) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Decedent">
                  {estateDetails?.decedent ? (
                    fullName(estateDetails.decedent)
                  ) : (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setIsDecedentModalVisible(true)}>
                      Add decedent
                    </Button>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Date of Death">
                  {estateDetails?.decedent?.dateOfDeath
                    ? moment(estateDetails.decedent.dateOfDeath).format('YYYY-MM-DD')
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="State">
                  {stateNameOf(estateDetails?.state) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="County">
                  {estateDetails?.county || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="EIN">
                  {estateDetails?.ein || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                  {estateDetails?.createdAt
                    ? new Date(estateDetails.createdAt).toLocaleString()
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Estate Address" span={2}>
                  {estateAddressText || estateDetails?.address || 'N/A'}
                </Descriptions.Item>
                {/* Labelled as the decedent's, because that is whose it is. */}
                {decedentAddressText && (
                  <Descriptions.Item label="Decedent Address" span={2}>
                    {decedentAddressText}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider />

              <Tabs defaultActiveKey="1">
                {/* Participants Tab */}
                <TabPane tab="Participants" key="1">
                  <List
                    dataSource={participants}
                    renderItem={(participant) => (
                      <ParticipantItem key={participant.id}>
                        <Avatar src={participant.profilePic}>
                          {!participant.profilePic && getInitials(participant.firstName, participant.lastName, participant.email)}
                        </Avatar>
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${participant.prefix || ''} ${participant.firstName} ${participant.middleName || ''} ${participant.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{participant.email}, </Text>
                          <Tag color="blue">{participant.addresses?.[0] && participant.addresses?.[0].city}</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(participant)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(participant.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Executors Tab */}
                <TabPane tab="Executors" key="2">
                  <List
                    dataSource={executors}
                    renderItem={(executor) => (
                      <ParticipantItem key={executor.id}>
                        <Avatar src={executor.profilePic}>
                          {!executor.profilePic && getInitials(executor.firstName, executor.lastName, executor.email)}
                        </Avatar>
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${executor.prefix || ''} ${executor.firstName} ${executor.middleName || ''} ${executor.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{executor.email}</Text>
                          <Tag color="green">{executor.isAdmin ? 'Admin' : 'Executor'}</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                        <Button 
            type="primary" 
            onClick={() => handleAddExecutorToParticipant(executor.uid)}
          >
            Add as Participant
          </Button>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(executor as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(executor.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Invited Guests Tab */}
                <TabPane tab="Invites" key="3">
                  <List
                    dataSource={invites}
                    renderItem={(invite) => (
                      <ParticipantItem key={invite.id}>
                        <Avatar src={invite.profilePic}>
                          {!invite.profilePic && getInitials(invite.firstName, invite.lastName, invite.email)}
                        </Avatar>
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${invite.prefix || ''} ${invite.firstName} ${invite.middleName || ''} ${invite.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{invite.email}</Text>
                          <Tag color="purple">Invites</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(invite as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleEditParticipant(invite as ParticipantResponse)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Invited Guests Tab */}
                <TabPane tab="Guests" key="4">
                  <List
                    dataSource={invitedGuests}
                    renderItem={(guest) => (
                      <ParticipantItem key={guest.id}>
                        <Avatar src={guest.profilePic}>
                          {!guest.profilePic && getInitials(guest.firstName, guest.lastName, guest.email)}
                        </Avatar>
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${guest.prefix || ''} ${guest.firstName} ${guest.middleName || ''} ${guest.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{guest.email}</Text>
                          <Tag color="purple">Guest</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(guest as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(guest.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>
              </Tabs>
            </>
          )}

          {/* Edit Estate Modal. The estate only: the owner and the decedent
              have their own forms below. */}
          {estateId && (
            <AddEstateModal
              open={isEditModalVisible}
              onCancel={() => setIsEditModalVisible(false)}
              onSubmit={handleEstateSubmit}
              initialValues={
                estateDetails
                  ? {
                    uuid: estateDetails.uuid,
                    estateName: estateDetails.estateName,
                    description: estateDetails.description,
                    ein: estateDetails.ein || '',
                    state: estateDetails.state || undefined,
                    county: estateDetails.county || '',
                    addressLine1: estateDetails.addressLine1 || '',
                    addressLine2: estateDetails.addressLine2 || '',
                    city: estateDetails.city || '',
                    zipCode: estateDetails.zipCode || '',
                  }
                  : undefined
              }
              isEdit={true}
            />
          )}

          {/* Edit Owner Modal */}
          {estateId && (
            <EditOwnerModal
              open={isEditOwnerModalVisible}
              estateId={estateId}
              owner={estateDetails?.estateOwner}
              onCancel={() => setIsEditOwnerModalVisible(false)}
              onSubmit={handleEstatePersonSaved}
            />
          )}

          {/* Add or Edit Decedent Modal */}
          {estateId && (
            <DecedentModal
              open={isDecedentModalVisible}
              estateId={estateId}
              decedent={estateDetails?.decedent}
              onCancel={() => setIsDecedentModalVisible(false)}
              onSubmit={handleEstatePersonSaved}
            />
          )}

          {/* Import/Export Modal */}
          {estateId && (
            <ImportExportModal
              open={isImportExportModalVisible}
              onClose={() => setIsImportExportModalVisible(false)}
              estateId={estateId!}
            />
          )}

          {/* Add or Edit Participant Modal */}
          <AddParticipantModal
            visible={isAddParticipantModalVisible}
            onCancel={() => {
              setIsAddParticipantModalVisible(false);
              setParticipantToEdit(null);
            }}
            onSuccess={handleParticipantSuccess}
            estateId={estateId!}
            estate={estateDetails}
            initialValues={participantToEdit || undefined}
            isEdit={participantToEdit ? true : false}
          />

          {/* Add Executor Modal */}
          {estateId && (
            <AddExecutorModal
              open={isAddExecutorModalVisible}
              onCancel={() => setIsAddExecutorModalVisible(false)}
              onSuccess={handleExecutorInviteSuccess}
              estateId={estateId!}
            />
          )}
        </EstateContainer>
      </Col>
      <Col span={8}>
        <AuditTrail />
      </Col>
    </Row>
  );
};

export default EstateDetails;
